import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLD from "@/components/feature/JsonLD";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { getAnimeByCategory, type AnimeItem } from "@/mocks/animeData";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "rexue");
  const [animeList, setAnimeList] = useState<AnimeItem[]>([]);

  const category = getCategoryBySlug(activeSlug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    const cat = categories.find((c) => c.slug === activeSlug);
    if (cat) {
      setAnimeList(getAnimeByCategory(cat.name));
    }
    window.scrollTo(0, 0);
  }, [activeSlug]);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://naishuidongman.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": category?.name || "分类",
        "item": `https://naishuidongman.com/category/${activeSlug}`,
      },
    ],
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category?.name || "动漫"}分类 - 奶水动漫在线`,
    "description": category?.description || "按分类浏览动漫",
    "url": `https://naishuidongman.com/category/${activeSlug}`,
  };

  const handleCategoryChange = (newSlug: string) => {
    if (newSlug !== activeSlug) {
      setActiveSlug(newSlug);
      // Navigate using the Link component handles this; but since we're using state,
      // we need to use window.history for smooth SPA transition without remount
      window.history.pushState(null, "", `/category/${newSlug}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      const cat = categories.find((c) => c.slug === newSlug);
      if (cat) {
        setAnimeList(getAnimeByCategory(cat.name));
      }
    }
  };

  return (
    <>
      <JsonLD data={breadcrumbSchema} />
      <JsonLD data={collectionSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: category?.name || "分类" },
              ]}
            />

            <div className="py-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                {category?.name}动漫
              </h1>
              <p className="text-sm text-foreground-600">{category?.description}</p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200/80"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 pb-14" data-product-shop>
              {animeList.map((anime) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={anime.cover}
                      alt={anime.title}
                      title={`${anime.title} - 奶水动漫在线高清观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                      {anime.score}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                      <p className="text-xs text-white/80">{anime.episodes}</p>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {anime.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{anime.updateInfo}</p>
                </Link>
              ))}
            </div>

            {animeList.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无动漫资源</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}